import { useEffect, useMemo, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Checkbox,
  Drawer,
  Group,
  Loader,
  MultiSelect,
  NumberInput,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconInfoCircle, IconTrash } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import type { ApiError} from '../../api/client';
import { api, unwrap } from '../../api/client';
import type { AlertRule } from '../../api/types';
import { useSession } from '../../app/session';
import { applyFieldErrors, notifyError, notifySaved } from '../../lib/notify';

type RecipientRow = {
  key: string;
  kind: string;
  person: string;
  role: string;
};

type FormValues = {
  name: string;
  description: string;
  metric: string;
  metric_parameter: string;
  subject_model: string;
  subject_id: string;
  threshold_mode: string;
  threshold: number | '';
  lead_times: string;
  lead_time_unit: string;
  severity: string;
  escalate_when_breached: boolean;
  channels: string[];
  cooldown_hours: number | '';
  is_active: boolean;
  recipients: RecipientRow[];
};

let recipientSeed = 0;
const newRecipientKey = () => `recipient-${++recipientSeed}`;

/**
 * Creating and editing an alert rule.
 *
 * The metric registry drives the form: choosing a metric decides which kinds of subject are
 * allowed and suggests the lead times, so a new metric on the backend needs no change here.
 */
export function AlertRuleDrawer({
  opened,
  onClose,
  ruleId,
  defaultSubject,
}: {
  opened: boolean;
  onClose: () => void;
  ruleId: number | null;
  defaultSubject?: { model: string; id: number };
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { enumChoices } = useSession();
  const [preview, setPreview] = useState<string | null>(null);

  const metrics = useQuery({
    queryKey: ['alerts', 'metrics'],
    queryFn: async () => unwrap(await api.GET('/api/v1/alerts/metrics/')),
    enabled: opened,
    staleTime: Infinity,
  });

  const rule = useQuery({
    queryKey: ['alerts', 'rule', ruleId],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/alerts/rules/{id}/', {
          params: { path: { id: ruleId as number } },
        }),
      ),
    enabled: opened && ruleId !== null,
  });

  const people = useQuery({
    queryKey: ['people', 'options'],
    queryFn: async () =>
      unwrap(await api.GET('/api/v1/people/', { params: { query: { page_size: 300 } } })),
    enabled: opened,
    staleTime: 5 * 60 * 1000,
  });

  const roles = useQuery({
    queryKey: ['roles'],
    queryFn: async () =>
      unwrap(await api.GET('/api/v1/roles/', { params: { query: { page_size: 100 } } })),
    enabled: opened,
    staleTime: 5 * 60 * 1000,
  });

  const form = useForm<FormValues>({
    initialValues: {
      name: '',
      description: '',
      metric: '',
      metric_parameter: '',
      subject_model: defaultSubject?.model ?? '',
      subject_id: defaultSubject ? String(defaultSubject.id) : '',
      threshold_mode: 'REMAINING',
      threshold: 0,
      lead_times: '',
      lead_time_unit: 'HOURS',
      severity: 'WARNING',
      escalate_when_breached: true,
      channels: ['IN_APP'],
      cooldown_hours: 24,
      is_active: true,
      recipients: [],
    },
    validate: {
      name: (value) => (value.trim() ? null : t('common.requiredField')),
      metric: (value) => (value ? null : t('common.requiredField')),
      subject_id: (value) => (value ? null : t('common.requiredField')),
    },
  });

  const record = rule.data;

  useEffect(() => {
    if (!opened) return;
    if (record) {
      form.setValues(toFormValues(record));
    } else {
      form.reset();
      form.setFieldValue('subject_model', defaultSubject?.model ?? '');
      form.setFieldValue('subject_id', defaultSubject ? String(defaultSubject.id) : '');
    }
    form.resetDirty();
    setPreview(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, record?.id]);

  const spec = useMemo(
    () => (metrics.data ?? []).find((entry) => entry.key === form.values.metric),
    [metrics.data, form.values.metric],
  );

  // Each metric declares which lead times make sense for it, which is a better starting point
  // than a global default of nothing.
  useEffect(() => {
    if (!spec || form.values.lead_times) return;
    form.setFieldValue('lead_times', spec.default_lead_times.join(', '));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spec?.key]);

  const subjects = useSubjectOptions(form.values.subject_model, opened);

  const save = useMutation({
    mutationFn: async (values: FormValues) => {
      const body = {
        name: values.name.trim(),
        description: values.description,
        subject: { model: values.subject_model, id: Number(values.subject_id) },
        metric: values.metric,
        metric_parameter: values.metric_parameter,
        threshold_mode: values.threshold_mode as AlertRule['threshold_mode'],
        threshold: String(values.threshold === '' ? 0 : values.threshold),
        lead_times: parseLeadTimes(values.lead_times),
        lead_time_unit: values.lead_time_unit as AlertRule['lead_time_unit'],
        severity: values.severity as AlertRule['severity'],
        escalate_when_breached: values.escalate_when_breached,
        channels: values.channels,
        cooldown_hours: values.cooldown_hours === '' ? 24 : values.cooldown_hours,
        is_active: values.is_active,
        recipients: values.recipients.map((row) => ({
          kind: row.kind as NonNullable<AlertRule['recipients']>[number]['kind'],
          person: row.person ? Number(row.person) : null,
          role: row.role ? Number(row.role) : null,
        })),
      };
      if (record) {
        return unwrap(
          await api.PATCH('/api/v1/alerts/rules/{id}/', {
            params: { path: { id: record.id } },
            body,
          }),
        );
      }
      return unwrap(await api.POST('/api/v1/alerts/rules/', { body }));
    },
    onSuccess: async () => {
      notifySaved();
      await queryClient.invalidateQueries({ queryKey: ['alerts'] });
      onClose();
    },
    onError: (error: ApiError) => {
      if (!applyFieldErrors(error, form.setErrors)) notifyError(error);
    },
  });

  const evaluate = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.POST('/api/v1/alerts/rules/{id}/evaluate/', {
          params: { path: { id: record!.id } },
        }),
      ),
    onSuccess: (data) => {
      const reading = data.reading;
      setPreview(
        reading
          ? `${reading.label}: ${reading.value ?? '—'}${
              reading.remaining ? ` · ${t('maintenance.remaining')} ${reading.remaining}` : ''
            }`
          : t('maintenance.OK'),
      );
    },
    onError: notifyError,
  });

  const remove = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.DELETE('/api/v1/alerts/rules/{id}/', {
          params: { path: { id: record!.id } },
        }),
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['alerts'] });
      onClose();
    },
    onError: notifyError,
  });

  const metricOptions = (metrics.data ?? []).map((entry) => ({
    value: entry.key,
    label: entry.label,
  }));

  const subjectModelOptions = (spec?.subject_models ?? []).map((model) => ({
    value: model,
    label: subjectModelLabel(model, t),
  }));

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="lg"
      title={record ? record.name : t('alerts.newRule')}
    >
      {rule.isLoading || metrics.isLoading ? (
        <Loader />
      ) : (
        <form onSubmit={form.onSubmit((values) => save.mutate(values))}>
          <Stack gap="md">
            <TextInput label={t('common.name')} required {...form.getInputProps('name')} />

            <Select
              label={t('alerts.metric')}
              required
              data={metricOptions}
              searchable
              allowDeselect={false}
              description={spec?.description}
              {...form.getInputProps('metric')}
            />

            <Group grow align="flex-start">
              <Select
                label={t('alerts.subject')}
                data={subjectModelOptions}
                allowDeselect={false}
                {...form.getInputProps('subject_model')}
              />
              <Select
                label=" "
                data={subjects}
                searchable
                allowDeselect={false}
                required
                {...form.getInputProps('subject_id')}
              />
            </Group>

            {spec?.kind === 'COUNTER' ? (
              <TextInput
                label={t('alerts.metricParameter')}
                description={t('alerts.metricParameterHint')}
                placeholder="AIRFRAME_HOURS"
                {...form.getInputProps('metric_parameter')}
              />
            ) : null}

            <Group grow align="flex-start">
              <Select
                label={t('alerts.thresholdMode')}
                data={enumChoices('threshold_mode').map((choice) => ({
                  value: choice.value,
                  label: choice.label,
                }))}
                allowDeselect={false}
                {...form.getInputProps('threshold_mode')}
              />
              <NumberInput
                label={t('alerts.threshold')}
                decimalScale={2}
                {...form.getInputProps('threshold')}
              />
            </Group>

            <Group grow align="flex-start">
              <TextInput
                label={t('alerts.leadTimes')}
                description={t('alerts.leadTimesHint')}
                placeholder="10, 5, 1"
                {...form.getInputProps('lead_times')}
              />
              <Select
                label={t('common.hours')}
                data={enumChoices('lead_time_unit').map((choice) => ({
                  value: choice.value,
                  label: choice.label,
                }))}
                allowDeselect={false}
                w={140}
                {...form.getInputProps('lead_time_unit')}
              />
            </Group>

            <Group grow align="flex-start">
              <Select
                label={t('alerts.severity')}
                data={enumChoices('alert_severity').map((choice) => ({
                  value: choice.value,
                  label: choice.label,
                }))}
                allowDeselect={false}
                {...form.getInputProps('severity')}
              />
              <MultiSelect
                label={t('alerts.channels')}
                data={enumChoices('alert_channel').map((choice) => ({
                  value: choice.value,
                  label: choice.label,
                }))}
                {...form.getInputProps('channels')}
              />
              <NumberInput
                label={t('alerts.cooldown')}
                min={0}
                max={720}
                {...form.getInputProps('cooldown_hours')}
              />
            </Group>

            <Checkbox
              label={t('alerts.escalate')}
              {...form.getInputProps('escalate_when_breached', { type: 'checkbox' })}
            />

            <Stack gap="xs">
              <Text fz="sm" fw={600}>
                {t('alerts.recipients')}
              </Text>
              {form.values.recipients.map((row, index) => (
                <Group key={row.key} gap="xs" wrap="nowrap" align="flex-start">
                  <Select
                    data={enumChoices('recipient_kind').map((choice) => ({
                      value: choice.value,
                      label: choice.label,
                    }))}
                    allowDeselect={false}
                    style={{ flex: 1 }}
                    {...form.getInputProps(`recipients.${index}.kind`)}
                  />
                  {row.kind === 'PERSON' ? (
                    <Select
                      data={(people.data?.results ?? []).map((person) => ({
                        value: String(person.id),
                        label: person.full_name,
                      }))}
                      searchable
                      style={{ flex: 1.4 }}
                      {...form.getInputProps(`recipients.${index}.person`)}
                    />
                  ) : null}
                  {row.kind === 'ROLE' ? (
                    <Select
                      data={(roles.data?.results ?? []).map((role) => ({
                        value: String(role.id),
                        label: role.name,
                      }))}
                      style={{ flex: 1.4 }}
                      {...form.getInputProps(`recipients.${index}.role`)}
                    />
                  ) : null}
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    mt={4}
                    onClick={() => form.removeListItem('recipients', index)}
                    aria-label={t('action.delete')}
                  >
                    <IconTrash size={16} />
                  </ActionIcon>
                </Group>
              ))}
              <Button
                variant="light"
                size="compact-sm"
                style={{ alignSelf: 'flex-start' }}
                onClick={() =>
                  form.insertListItem('recipients', {
                    key: newRecipientKey(),
                    kind: 'MAINTAINERS',
                    person: '',
                    role: '',
                  })
                }
              >
                {t('action.add')}
              </Button>
            </Stack>

            <Textarea
              label={t('common.notes')}
              autosize
              minRows={2}
              {...form.getInputProps('description')}
            />

            <Checkbox
              label={t('common.active')}
              {...form.getInputProps('is_active', { type: 'checkbox' })}
            />

            {record?.current_reading ? (
              <Alert color="blue" variant="light" icon={<IconInfoCircle size={17} />}>
                <Group gap="xs">
                  <Text fz="sm">
                    {t('alerts.currentReading')}: {record.current_reading.value ?? '—'}
                  </Text>
                  {record.current_reading.remaining ? (
                    <Badge size="sm" variant="light">
                      {t('maintenance.remaining')} {record.current_reading.remaining}
                    </Badge>
                  ) : null}
                </Group>
              </Alert>
            ) : null}

            {preview ? (
              <Alert color="teal" variant="light">
                {preview}
              </Alert>
            ) : null}

            <Group justify="space-between">
              <Group gap="xs">
                {record ? (
                  <>
                    <Button
                      variant="light"
                      loading={evaluate.isPending}
                      onClick={() => evaluate.mutate()}
                    >
                      {t('alerts.evaluateNow')}
                    </Button>
                    {!record.is_system ? (
                      <Button
                        variant="subtle"
                        color="red"
                        loading={remove.isPending}
                        onClick={() => remove.mutate()}
                      >
                        {t('action.delete')}
                      </Button>
                    ) : null}
                  </>
                ) : null}
              </Group>
              <Group gap="xs">
                <Button variant="default" onClick={onClose}>
                  {t('action.cancel')}
                </Button>
                <Button type="submit" loading={save.isPending}>
                  {t('action.save')}
                </Button>
              </Group>
            </Group>
          </Stack>
        </form>
      )}
    </Drawer>
  );
}

/** The pickable subjects for the chosen model. Only the three that rules are written about. */
function useSubjectOptions(model: string, enabled: boolean) {
  const aircraft = useQuery({
    queryKey: ['aircraft', 'options'],
    queryFn: async () =>
      unwrap(await api.GET('/api/v1/aircraft/', { params: { query: { page_size: 200 } } })),
    enabled: enabled && model === 'aircraft.aircraft',
    staleTime: 5 * 60 * 1000,
  });

  const tasks = useQuery({
    queryKey: ['maintenance', 'tasks', 'options'],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/maintenance/tasks/', { params: { query: { page_size: 300 } } }),
      ),
    enabled: enabled && model === 'maintenance.maintenancetask',
    staleTime: 5 * 60 * 1000,
  });

  const people = useQuery({
    queryKey: ['people', 'options'],
    queryFn: async () =>
      unwrap(await api.GET('/api/v1/people/', { params: { query: { page_size: 300 } } })),
    enabled: enabled && model === 'staff.person',
    staleTime: 5 * 60 * 1000,
  });

  if (model === 'aircraft.aircraft') {
    return (aircraft.data?.results ?? []).map((row) => ({
      value: String(row.id),
      label: `${row.registration} · ${row.type_label}`,
    }));
  }
  if (model === 'maintenance.maintenancetask') {
    return (tasks.data?.results ?? []).map((row) => ({
      value: String(row.id),
      label: `${row.registration} · ${row.code} ${row.name}`,
    }));
  }
  if (model === 'staff.person') {
    return (people.data?.results ?? []).map((row) => ({
      value: String(row.id),
      label: row.full_name,
    }));
  }
  return [];
}

function subjectModelLabel(model: string, t: (key: string) => string) {
  switch (model) {
    case 'aircraft.aircraft':
      return t('aircraft.title');
    case 'maintenance.maintenancetask':
      return t('maintenance.task');
    case 'staff.person':
      return t('staff.person');
    case 'staff.personlicence':
      return t('staff.licences');
    case 'staff.personrating':
      return t('staff.ratings');
    case 'staff.personmedical':
      return t('staff.medicals');
    case 'staff.languageproficiency':
      return t('staff.language');
    case 'staff.personcertificate':
      return t('staff.certificates');
    case 'staff.currencyrule':
      return t('staff.currencyRule');
    case 'organisations.organisation':
      return t('common.organisation');
    default:
      return model;
  }
}

function parseLeadTimes(value: string) {
  return value
    .split(/[,;\s]+/)
    .map((entry) => Number(entry.trim()))
    .filter((entry) => Number.isFinite(entry) && entry >= 0);
}

function toFormValues(rule: AlertRule): FormValues {
  return {
    name: rule.name,
    description: rule.description ?? '',
    metric: rule.metric,
    metric_parameter: rule.metric_parameter ?? '',
    subject_model: rule.subject?.model ?? '',
    subject_id: rule.subject ? String(rule.subject.id) : '',
    threshold_mode: rule.threshold_mode ?? 'REMAINING',
    threshold: rule.threshold ? Number(rule.threshold) : 0,
    lead_times: (rule.lead_times ?? []).join(', '),
    lead_time_unit: rule.lead_time_unit ?? 'HOURS',
    severity: rule.severity ?? 'WARNING',
    escalate_when_breached: rule.escalate_when_breached ?? true,
    channels: rule.channels ?? ['IN_APP'],
    cooldown_hours: rule.cooldown_hours ?? 24,
    is_active: rule.is_active ?? true,
    recipients: (rule.recipients ?? []).map((row) => ({
      key: newRecipientKey(),
      kind: row.kind ?? 'PERSON',
      person: row.person ? String(row.person) : '',
      role: row.role ? String(row.role) : '',
    })),
  };
}
